import { Dialog } from '@mui/material';
import { OptionallyVisible } from 'components';
import { Form, Formik, FormikErrors, FormikTouched } from 'formik';
import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MerchantInfo } from 'services/merchants/types';
import * as Yup from 'yup';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';
import { SelectItem } from 'components/inputs/select/select';

import { Response, SuccessResponse } from 'types/http';

import { stripFilesRecursive } from '../../../utils/stripFiles';
import { BridgeKycInfoProvider } from '../../BridgeKycInfoContext';
import { FormType, LABELS, VALIDATION_SCHEMAS } from '../../keys';
import { UseSessionFormResult } from '../../useSessionForm';
import { getInitialFormValues } from '../../utils/getInitialFormValues';
import { getStepWithError } from '../../utils/mapBackendErrorsToFormFields';
import Step1 from '../step1/step1';
import Step2 from '../step2/step2';
import Step3 from '../step3/step3';
import { FormControls } from './components/formControls/formControls';
import SettingsSwitch from './components/settingsSwitch/settingsSwitch';
import { Buttons, Delimeter, ModalRoot, PanelContent } from './styles';
import useStepper, { MAX_STEP, MIN_STEP } from './useStepper';
import { normalizeFormValues } from './utils/normalizeFormValues';

interface MerchantKybModalProps {
  merchant: MerchantInfo;
  onConfirm: (merchantKybPayload: Partial<FormType>) => Promise<void>;
  isOpen: boolean;
  closeModal: () => void;
  countries: SelectItem[];
  //  for errors coming from backend
  error?: Record<string, string>;
  isUpdatingMerchantKyb: boolean;
  merchantKybData?: Response<SuccessResponse>;
  clearBackendErrors?: () => void;
  sessionControllers: UseSessionFormResult;
  onInsertTitanData: () => void;
  titanFormData?: FormType;
  isLoadingTitanData?: boolean;
  bridgeKycInfo?: import('services/merchants/types').BridgeKycInfoData | null;
}

interface FormikValuesObserverProps {
  values: FormType;
  resetForm: (props: { values: FormType }) => void;
}

const makeSchemaOptional = (schema: Yup.ObjectSchema<any>): Yup.ObjectSchema<any> => {
  const shape = schema.describe().fields;
  const optionalShape: Record<string, any> = {};

  Object.keys(shape).forEach((key) => {
    // All fields become optional in partial mode
    // Disabled fields are controlled by UI components using isFieldDisabledInPartialMode
    optionalShape[key] = Yup.mixed().optional();
  });

  return Yup.object().shape(optionalShape);
};

export const MerchantKybModal = ({
  onConfirm,
  isOpen,
  closeModal,
  merchant,
  countries,
  error,
  isUpdatingMerchantKyb,
  merchantKybData,
  clearBackendErrors,
  sessionControllers,
  onInsertTitanData,
  titanFormData,
  isLoadingTitanData,
  bridgeKycInfo,
}: PropsWithChildren<MerchantKybModalProps>) => {
  const { t } = useTranslation();
  const { step, next, prev, reset, go } = useStepper();
  const [isCompleteDataRequired, setIsCompleteDataRequired] = useState(true);
  const lastHandledRequestId = useRef<string | null>(null);

  const validationSchema = useMemo(() => {
    const baseSchema = VALIDATION_SCHEMAS[step - 1];
    if (!baseSchema) {
      return Yup.object().shape({});
    }
    return isCompleteDataRequired ? baseSchema : makeSchemaOptional(baseSchema as any);
  }, [step, isCompleteDataRequired]);

  const isSuccess = merchantKybData?.data?.success;
  const hasErrors = error ? Object.keys(error).length > 0 : false;
  const currentRequestId = merchantKybData?.requestId;

  useEffect(() => {
    if (hasErrors) {
      go(getStepWithError(error ?? {}) ?? MIN_STEP);
    } else if (isSuccess && !hasErrors && currentRequestId && currentRequestId !== lastHandledRequestId.current) {
      lastHandledRequestId.current = currentRequestId;
      reset();
      resetSessionForm();
      closeModal();
    }
  }, [merchantKybData, error, isSuccess, hasErrors]);

  const submitForm = async (values: Partial<FormType>) => {
    await onConfirm(values);
  };

  const getChangedValues = (currentValues: FormType): Partial<FormType> => {
    const changes: Partial<FormType> = {};
    const normalizedCurrent = normalizeFormValues(currentValues);
    const normalizedOriginal = normalizeFormValues(getInitialFormValues(merchant.name, merchant.uuid));

    Object.keys(normalizedCurrent).forEach((key) => {
      const currentValue = (normalizedCurrent as any)[key];
      const originalValue = (normalizedOriginal as any)[key];

      const isChanged = JSON.stringify(currentValue) !== JSON.stringify(originalValue);

      if (isChanged) {
        (changes as any)[key] = currentValue;
      }
    });

    return changes;
  };

  const handleNextButtonClick = (values: FormType) => {
    if (step < MAX_STEP) {
      next();
    } else {
      const payload = isCompleteDataRequired ? normalizeFormValues(values) : getChangedValues(values);
      submitForm(payload);
    }
  };

  const { sessionValues, handleFormValueChange, resetSessionForm, refreshSessionValues } = sessionControllers;

  useEffect(() => {
    if (isOpen) {
      refreshSessionValues();
    }
  }, [isOpen, refreshSessionValues]);

  useEffect(() => {
    if (sessionValues.uuid !== merchant.uuid) {
      resetSessionForm();
    }
  }, [merchant.uuid]);

  const FormikValuesObserver = ({ values, resetForm }: FormikValuesObserverProps) => {
    const lastSerialized = useRef<string>(JSON.stringify(stripFilesRecursive(values)));
    const lastSessionSerialized = useRef<string>(JSON.stringify(sessionValues));
    const isResetting = useRef<boolean>(false);

    useEffect(() => {
      if (isResetting.current) {
        isResetting.current = false;
        return;
      }

      const newSerialized = JSON.stringify(stripFilesRecursive(values));
      if (lastSerialized.current !== newSerialized) {
        handleFormValueChange(values);
        lastSerialized.current = newSerialized;
      }
    }, [values]);

    useEffect(() => {
      const sessionSerialized = JSON.stringify(sessionValues);
      if (lastSessionSerialized.current !== sessionSerialized) {
        const currentSerialized = JSON.stringify(stripFilesRecursive(values));
        if (currentSerialized !== sessionSerialized) {
          isResetting.current = true;
          resetForm({ values: sessionValues });
          lastSerialized.current = sessionSerialized;
        }
        lastSessionSerialized.current = sessionSerialized;
      }
    }, [sessionValues]);

    return null;
  };

  const ValidationErrorsCleaner = ({
    setErrors,
    setTouched,
  }: {
    setErrors: (errors: FormikErrors<FormType>) => void;
    setTouched: (touched: FormikTouched<FormType>) => void;
  }) => {
    useEffect(() => {
      if (!isCompleteDataRequired) {
        setErrors({});
        setTouched({});
      }
    }, [isCompleteDataRequired, setErrors, setTouched]);

    return null;
  };

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} scroll="body" disableEscapeKeyDown>
      <ModalRoot>
        <Formik
          key={JSON.stringify(sessionValues)}
          initialValues={sessionValues}
          validationSchema={validationSchema}
          onSubmit={handleNextButtonClick}
          validateOnMount={false}
        >
          {({ values, resetForm, setErrors, setTouched }) => {
            const handleResetData = () => {
              const initialValues = getInitialFormValues(merchant.name, merchant.uuid);
              resetSessionForm();
              resetForm({ values: initialValues });
            };

            return (
              <Panel
                label={t(LABELS.MODAL.TITLE)}
                controls={
                  <FormControls
                    closeModal={closeModal}
                    resetData={handleResetData}
                    onInsertTitanData={onInsertTitanData}
                    titanFormData={titanFormData}
                    isLoadingTitanData={isLoadingTitanData}
                  />
                }
              >
                <SettingsSwitch value={isCompleteDataRequired} setValue={setIsCompleteDataRequired} />
                <Form>
                  <FormikValuesObserver values={values} resetForm={resetForm} />
                  <ValidationErrorsCleaner setErrors={setErrors} setTouched={setTouched} />
                  <BridgeKycInfoProvider value={bridgeKycInfo}>
                    <PanelContent>
                      <OptionallyVisible visible={step === 1}>
                        <Step1 error={error} countries={countries} isPartialMode={!isCompleteDataRequired} />
                      </OptionallyVisible>
                      <OptionallyVisible visible={step === 2}>
                        <Step2
                          error={error}
                          countries={countries}
                          clearBackendErrors={clearBackendErrors}
                          isPartialMode={!isCompleteDataRequired}
                        />
                      </OptionallyVisible>
                      <OptionallyVisible visible={step === 3}>
                        <Step3 error={error} countries={countries} isPartialMode={!isCompleteDataRequired} />
                      </OptionallyVisible>
                      <Delimeter />
                      <Buttons>
                        {step > MIN_STEP ? (
                          <Button onClick={prev}> {t(LABELS.MODAL.BACK)}</Button>
                        ) : (
                          <Button secondary onClick={closeModal}>
                            {t(LABELS.MODAL.CANCEL)}
                          </Button>
                        )}
                        <Button primary onClick={() => {}} type="submit" disabled={isUpdatingMerchantKyb}>
                          {step < MAX_STEP ? t(LABELS.MODAL.CONTINUE) : t(LABELS.MODAL.CONFIRM)}
                        </Button>
                      </Buttons>
                    </PanelContent>
                  </BridgeKycInfoProvider>
                </Form>
              </Panel>
            );
          }}
        </Formik>
      </ModalRoot>
    </Dialog>
  );
};
